import { createSlice, PayloadAction, nanoid } from '@reduxjs/toolkit';
import { DEFAULT_FY_KEY, drillDownKey, fyKey } from './constants';
import type { DrillDownEntry, DrillDownRow, FetchStatus, ReconciliationsEntry, ReconciliationsPayload } from './types';

interface ReconciliationsState {
    // keyed `${orgId}|${fyKey}`
    entries: Record<string, ReconciliationsEntry>;
    // keyed `${orgId}|${fyKey}|${reconId}:${fy}`
    drillDowns: Record<string, DrillDownEntry>;
}

const initialState: ReconciliationsState = {
    entries: {},
    drillDowns: {},
};

const entryKey = (orgId: string, fys: number[] | null) => `${orgId}|${fyKey(fys)}`;
const drillEntryKey = (orgId: string, fys: number[] | null, reconId: string, fy: number) =>
    `${orgId}|${fyKey(fys)}|${drillDownKey(reconId, fy)}`;

const isClaimable = (status: FetchStatus | undefined) => !status || status === 'idle' || status === 'error';

const reconciliationsSlice = createSlice({
    name: 'reconciliations',
    initialState,
    reducers: {
        // The reducer itself is the de-dup point (D26-7): an entry already loading/succeeded
        // is left untouched, so a chip + pill mounting in the same render only ever claims once.
        ensureReconciliations: {
            reducer: (state, action: PayloadAction<{ orgId: string; fys: number[] | null; requestToken: string }>) => {
                const key = entryKey(action.payload.orgId, action.payload.fys);
                const existing = state.entries[key];
                if (!isClaimable(existing?.status)) return;
                state.entries[key] = { status: 'loading', token: action.payload.requestToken, payload: existing?.payload ?? null, error: null };
            },
            prepare: (payload: { orgId: string; fys: number[] | null }) => ({
                payload: { ...payload, requestToken: nanoid() },
            }),
        },
        fetchReconciliationsSucceeded: (
            state,
            action: PayloadAction<{ orgId: string; fys: number[] | null; payload: ReconciliationsPayload; requestToken: string }>,
        ) => {
            const key = entryKey(action.payload.orgId, action.payload.fys);
            const entry = state.entries[key];
            if (!entry || entry.token !== action.payload.requestToken || !action.payload.payload) return;
            state.entries[key] = { status: 'success', token: entry.token, payload: action.payload.payload, error: null };

            // The page-load response (fys: null) is also stored under its own effective-FY key,
            // so opening the drawer with the default selection maps back to this same fetch
            // (D26-6) — no second request when the FY multiselect shows its own default set.
            if (fyKey(action.payload.fys) === DEFAULT_FY_KEY) {
                const effectiveKey = entryKey(action.payload.orgId, action.payload.payload.fys);
                state.entries[effectiveKey] = { status: 'success', token: entry.token, payload: action.payload.payload, error: null };
            }
        },
        fetchReconciliationsFailed: (state, action: PayloadAction<{ orgId: string; fys: number[] | null; requestToken: string; error: string }>) => {
            const key = entryKey(action.payload.orgId, action.payload.fys);
            const entry = state.entries[key];
            if (!entry || entry.token !== action.payload.requestToken) return;
            state.entries[key] = { status: 'error', token: entry.token, payload: null, error: action.payload.error };
        },
        // Company-view unmount / org change (useEnsureReconciliations cleanup): drop every
        // entry for that org so a late response for the old org fails its token check.
        releaseReconciliations: (state, action: PayloadAction<{ orgId: string }>) => {
            Object.keys(state.entries).forEach((key) => {
                if (key.startsWith(`${action.payload.orgId}|`)) delete state.entries[key];
            });
            Object.keys(state.drillDowns).forEach((key) => {
                if (key.startsWith(`${action.payload.orgId}|`)) delete state.drillDowns[key];
            });
        },
        ensureDrillDown: {
            reducer: (
                state,
                action: PayloadAction<{ orgId: string; fys: number[] | null; reconId: string; fy: number; requestToken: string }>,
            ) => {
                const key = drillEntryKey(action.payload.orgId, action.payload.fys, action.payload.reconId, action.payload.fy);
                const existing = state.drillDowns[key];
                if (!isClaimable(existing?.status)) return;
                state.drillDowns[key] = { status: 'loading', token: action.payload.requestToken, rows: existing?.rows ?? null, error: null };
            },
            prepare: (payload: { orgId: string; fys: number[] | null; reconId: string; fy: number }) => ({
                payload: { ...payload, requestToken: nanoid() },
            }),
        },
        fetchDrillDownSucceeded: (
            state,
            action: PayloadAction<{ orgId: string; fys: number[] | null; reconId: string; fy: number; rows: DrillDownRow[]; requestToken: string }>,
        ) => {
            const key = drillEntryKey(action.payload.orgId, action.payload.fys, action.payload.reconId, action.payload.fy);
            const entry = state.drillDowns[key];
            if (!entry || entry.token !== action.payload.requestToken) return;
            state.drillDowns[key] = { status: 'success', token: entry.token, rows: action.payload.rows, error: null };
        },
        fetchDrillDownFailed: (
            state,
            action: PayloadAction<{ orgId: string; fys: number[] | null; reconId: string; fy: number; requestToken: string; error: string }>,
        ) => {
            const key = drillEntryKey(action.payload.orgId, action.payload.fys, action.payload.reconId, action.payload.fy);
            const entry = state.drillDowns[key];
            if (!entry || entry.token !== action.payload.requestToken) return;
            state.drillDowns[key] = { status: 'error', token: entry.token, rows: null, error: action.payload.error };
        },
    },
});

export const {
    ensureReconciliations,
    fetchReconciliationsSucceeded,
    fetchReconciliationsFailed,
    releaseReconciliations,
    ensureDrillDown,
    fetchDrillDownSucceeded,
    fetchDrillDownFailed,
} = reconciliationsSlice.actions;

export { entryKey, drillEntryKey };
export default reconciliationsSlice.reducer;
